// Chat sessions store — server-backed (Pilar: conversaciones persistentes).
//
// Sessions live in the gateway (chat_sessions / chat_messages, RLS), scoped
// to `global` (the general /chat) or to a specific design/dashboard (the
// Designer chat), and are private or shared with that scope's collaborators.
// This replaces the old per-browser localStorage history: threads now
// survive reloads, sync across devices, and — when shared — are visible to
// everyone on the design.

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  listChatSessions, createChatSession, updateChatSession, deleteChatSession,
  getChatMessages, appendChatMessage,
  type ChatScopeKind, type ChatVisibility, type ChatSessionDto,
} from '../api/client';

export type ImgMedia = 'image/png' | 'image/jpeg' | 'image/webp';
export interface ChatAttachment {
  mediaType: ImgMedia;
  /** data: URL for preview rendering (not persisted to the server). */
  dataUrl: string;
  /** Base64 payload (no data: prefix) sent to the model + stored. */
  dataBase64: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  /** Plain text for user / system + final assistant; trace blocks for live assistant turns. */
  content: string | TraceBlock[];
  images?: ChatAttachment[];
}

export interface TraceBlock {
  type: 'text' | 'tool_use' | 'tool_result' | 'ui_resource' | 'confirm';
  text?: string;
  tool_call_id?: string;
  name?: string;
  input?: Record<string, unknown>;
  output?: unknown;
  is_error?: boolean;
  // confirm: the assistant proposed a state-changing tool; the user must confirm
  // (ConfirmCard) before it runs via /api/chat/tools/confirm. `status` tracks the
  // card lifecycle; `result` holds the executed tool's output.
  status?: 'pending' | 'running' | 'done' | 'cancelled' | 'error';
  result?: unknown;
  // MCP App (ui_resource): an interactive design surfaced by a tool result and
  // rendered inline in a sandboxed iframe (ChatAppFrame). `kind`/`id` identify the
  // design; `uri` is the `ui://` resource id. Two render modes:
  //  - `url`  → an embed URL (live dashboard) loaded as the iframe src;
  //  - `html` → self-contained HTML (designer_render_view) shown via srcdoc.
  // The bridge refreshes through the originating tool (show_dashboard / render).
  //  - kind `app` → a purpose-built MCP App (ADR 33 F3) loaded sandboxed from
  //    `url` (same-origin file, null-origin sandbox), talking via the App SDK.
  app?: { kind: 'dashboard' | 'design' | 'app'; id: string; uri?: string; url?: string; html?: string };
}

export interface SessionSummary {
  id: string;
  title: string;
  visibility: ChatVisibility;
  scopeKind: ChatScopeKind;
  scopeId: string | null;
  createdBy: string;
  updatedAt: string;
}

function toSummary(d: ChatSessionDto): SessionSummary {
  return {
    id: d.id, title: d.title, visibility: d.visibility,
    scopeKind: d.scope_kind, scopeId: d.scope_id, createdBy: d.created_by, updatedAt: d.updated_at,
  };
}

export const useChatStore = defineStore('chat', () => {
  const scopeKind = ref<ChatScopeKind>('global');
  const scopeId = ref<string | null>(null);
  const sessions = ref<SessionSummary[]>([]);
  const activeId = ref<string | null>(null);
  const messages = ref<ChatMessage[]>([]);
  const loadingSessions = ref(false);
  const loadingMessages = ref(false);
  // Chat actions — per-session override for the confirm gate: null = use the
  // tenant default; true/false = force auto-run on/off for this session.
  const autoRun = ref<boolean | null>(null);

  const activeSession = computed(() => sessions.value.find((s) => s.id === activeId.value) ?? null);

  /** Point the store at a scope (global, or a design) and load its sessions. */
  async function setScope(kind: ChatScopeKind, id: string | null = null): Promise<void> {
    if (scopeKind.value === kind && scopeId.value === id && sessions.value.length) return;
    scopeKind.value = kind;
    scopeId.value = id;
    activeId.value = null;
    messages.value = [];
    // autoRun is a per-session override; a scope switch must not carry a
    // design chat's "run tools without confirming" into another scope, or the
    // gate would auto-execute mutating tools the user never opted into there.
    autoRun.value = null;
    await loadSessions();
  }

  async function loadSessions(): Promise<void> {
    loadingSessions.value = true;
    try {
      const list = await listChatSessions(scopeKind.value, scopeId.value);
      sessions.value = list.map(toSummary);
    } finally {
      loadingSessions.value = false;
    }
  }

  async function selectSession(id: string): Promise<void> {
    if (id === activeId.value) return;
    activeId.value = id;
    messages.value = [];
    autoRun.value = null; // per-session override — don't carry it across sessions
    loadingMessages.value = true;
    try {
      const { messages: msgs } = await getChatMessages(id);
      messages.value = msgs.map((m) => ({
        role: m.role,
        content: m.content as string | TraceBlock[],
        ...(m.images?.length
          ? { images: m.images.map((i) => ({ mediaType: i.mediaType as ImgMedia, dataBase64: i.dataBase64, dataUrl: `data:${i.mediaType};base64,${i.dataBase64}` })) }
          : {}),
      }));
    } finally {
      loadingMessages.value = false;
    }
  }

  async function newSession(opts: { title?: string; visibility?: ChatVisibility; keepMessages?: boolean } = {}): Promise<SessionSummary> {
    const created = await createChatSession({
      scope_kind: scopeKind.value,
      scope_id: scopeId.value,
      ...(opts.title ? { title: opts.title } : {}),
      ...(opts.visibility ? { visibility: opts.visibility } : {}),
    });
    const summary = toSummary(created);
    sessions.value = [summary, ...sessions.value];
    activeId.value = summary.id;
    // The explicit "New chat" action starts an empty thread. Lazy creation
    // during a send (keepMessages) must NOT wipe the optimistic user message
    // that was just pushed — otherwise the first turn builds an empty
    // messages[] payload and the gateway rejects it (400 messages[] required).
    // Explicit "New chat" starts fresh (default gate); lazy mid-send creation
    // (keepMessages) preserves the choice made for the in-flight turn.
    if (!opts.keepMessages) { messages.value = []; autoRun.value = null; }
    return summary;
  }

  /** Create a session lazily on first use; returns the active id. Keeps any
   *  already-pushed (optimistic) messages — it runs mid-send. */
  async function ensureSession(): Promise<string> {
    if (activeId.value) return activeId.value;
    const s = await newSession({ keepMessages: true });
    return s.id;
  }

  async function deleteSession(id: string): Promise<void> {
    await deleteChatSession(id);
    sessions.value = sessions.value.filter((s) => s.id !== id);
    if (activeId.value === id) {
      activeId.value = null;
      messages.value = [];
      autoRun.value = null;
    }
  }

  async function rename(id: string, title: string): Promise<void> {
    const updated = await updateChatSession(id, { title });
    patchLocal(updated);
  }

  async function setVisibility(id: string, visibility: ChatVisibility): Promise<void> {
    const updated = await updateChatSession(id, { visibility });
    patchLocal(updated);
  }

  function patchLocal(d: ChatSessionDto): void {
    const s = toSummary(d);
    sessions.value = sessions.value.map((x) => (x.id === s.id ? s : x));
  }

  /** Optimistic local push (UI renders immediately); returns the reactive entry. */
  function pushLocal(msg: ChatMessage): ChatMessage {
    messages.value = [...messages.value, msg];
    return messages.value[messages.value.length - 1]!;
  }

  /** Persist one message (string content + optional images). `sessionId` pins
   *  the target session (a streaming turn persists against the session it
   *  STARTED in, even if the user switched mid-stream); omitted → the active
   *  session, created lazily. */
  async function persist(msg: { role: ChatMessage['role']; content: unknown; images?: ChatAttachment[] }, sessionId?: string): Promise<void> {
    const id = sessionId ?? await ensureSession();
    await appendChatMessage(id, {
      role: msg.role,
      content: msg.content,
      ...(msg.images?.length ? { images: msg.images.map((i) => ({ mediaType: i.mediaType, dataBase64: i.dataBase64 })) } : {}),
    });
    // Optimistic title from the first user message + bump recency locally.
    const s = sessions.value.find((x) => x.id === id);
    if (s) {
      if (msg.role === 'user' && typeof msg.content === 'string' && s.title === 'New chat') s.title = truncateTitle(msg.content);
      s.updatedAt = new Date().toISOString();
      sessions.value = [s, ...sessions.value.filter((x) => x.id !== id)];
    }
  }

  return {
    scopeKind, scopeId, sessions, activeId, messages, loadingSessions, loadingMessages,
    autoRun, activeSession,
    setScope, loadSessions, selectSession, newSession, ensureSession,
    deleteSession, rename, setVisibility, pushLocal, persist,
  };
});

function truncateTitle(s: string, max = 60): string {
  const cleaned = s.replace(/\s+/g, ' ').trim();
  return cleaned.length <= max ? cleaned : cleaned.slice(0, max - 1) + '…';
}
